import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { getPartnerCompanyById } from '../../api/scoped-queries.js';
import type { SmartTeamsCompany } from '../../db/index.js';

// Cache for company data to avoid repeated queries
const companyCache = new Map<string, SmartTeamsCompany | null>();

const renderCompany = (company: SmartTeamsCompany | null, fallbackId: string) => {
  if (!company) {
    return html`<span class="text-base-content/50 text-xs overflow-hidden whitespace-nowrap">${fallbackId}</span>`;
  }

  return html`
    <div class="flex flex-col w-full min-w-0">
      <span class="text-sm block truncate" title=${company.name ?? ''}>${company.name}</span>
      ${company.address?.zipCity?.city
        ? html`<span class="text-xs text-base-content/50 block truncate" title=${company.address.zipCity.city}
            >${company.address.zipCity.city}</span
          >`
        : nothing}
    </div>
  `;
};

/**
 * A component that resolves a company ID to its name.
 * Uses backend query for single company lookup with caching.
 */
export const PartnerCompanyName = stateful(({ companyId }: { companyId?: string }) => {
  if (!companyId) {
    return html`<span class="text-base-content/50">-</span>`;
  }

  // Check cache first
  const cachedCompany = companyCache.get(companyId);
  if (cachedCompany !== undefined) {
    return renderCompany(cachedCompany, companyId);
  }

  // Query single company by ID (with permission check)
  const companyQuery = useQuery(
    () => getPartnerCompanyById(companyId)({ _id: 1, name: 1, address: { zipCity: { city: 1 } } }),
    [companyId],
  );

  if (companyQuery.loading) {
    return html`<span class="loading loading-spinner loading-xs"></span>`;
  }

  const company = companyQuery.value as SmartTeamsCompany | null;

  // Cache the result
  companyCache.set(companyId, company);

  return renderCompany(company, companyId);
});
